import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICmdbCiVMInstance } from '../../../services/serviceNow/cmdbCiVMInstance/cmdbCiVMInstance.model';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { RootState } from '../../app.model';
import {
  deleteCmdbCiVMInstance,
  getCmdbCiVMInstanceById,
  searchCmdbCiVMInstance,
  saveCmdbCiVMInstance,
  processDataCmdbCiVMInstance,
} from './cmdbCiVMInstance.action';
import { ICmdbCiVMInstanceState } from './cmdbCiVMInstance.model';

export const initialState: ICmdbCiVMInstanceState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  processData: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const CmdbCiVMInstanceSlice = createSlice({
  name: 'CmdbCiVMInstance',
  initialState,
  reducers: {
    clearCmdbCiVMInstance: () => {
      return initialState;
    },
    clearCmdbCiVMInstanceMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
      state.processData.messages = [];
    },
    clearCmdbCiVMInstanceGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchCmdbCiVMInstance.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchCmdbCiVMInstance.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<ICmdbCiVMInstance>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchCmdbCiVMInstance.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getCmdbCiVMInstanceById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getCmdbCiVMInstanceById.fulfilled.type]: (state, action: PayloadAction<ICmdbCiVMInstance>) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getCmdbCiVMInstanceById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveCmdbCiVMInstance.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveCmdbCiVMInstance.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveCmdbCiVMInstance.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteCmdbCiVMInstance.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteCmdbCiVMInstance.fulfilled.type]: (state, action: PayloadAction<IApiResponseBody<unknown>>) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteCmdbCiVMInstance.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },

    // Process Data
    [processDataCmdbCiVMInstance.pending.type]: (state) => {
      state.processData.loading = true;
      state.processData.messages = [];
    },
    [processDataCmdbCiVMInstance.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.processData.loading = false;
      state.processData.hasErrors = false;
      state.processData.messages = action.payload.messages;
    },
    [processDataCmdbCiVMInstance.rejected.type]: (state) => {
      state.processData.loading = false;
      state.processData.hasErrors = true;
    },
  },
});

// A selector
export const CmdbCiVMInstanceSelector = (state: RootState) => state.cmdbCiVMInstance;

// Actions
export const {
  clearCmdbCiVMInstance,
  clearCmdbCiVMInstanceMessages,
  clearCmdbCiVMInstanceGetById,
  setTableColumnSelection,
} = CmdbCiVMInstanceSlice.actions;

// The reducer
export default CmdbCiVMInstanceSlice.reducer;
